import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/tables/Table";
import { Gig, UserRole, VenueProfile, User, ArtistProfile } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { API_BASE_URL } from '@/config';
import { useLogger } from '@/contexts/LogContext';
import { LogLevel, LogCategory } from '@/types/logs';
import { Button } from '@/components/ui/Button';

interface MockBooking {
    id: string;
    artist: string;
    event: string;
    status: 'Pending' | 'Confirmed' | 'Cancelled';
}

const mockBookings: MockBooking[] = [
    {
      id: 'RB-101',
      artist: 'Artist B',
      event: 'Event D',
      status: 'Pending',
    },
    {
      id: 'RB-102',
      artist: 'Artist C',
      event: 'Event E',
      status: 'Confirmed',
    },
];

const ActionButton: React.FC<{ onClick: () => void; className: string; children: React.ReactNode }> = ({ onClick, className, children }) => (
  <button
    onClick={onClick}
    className={`text-xs font-semibold px-2.5 py-1 rounded-md transition-colors ${className}`}>
    {children}
  </button>
);

const TableActions: React.FC<{ 
  gig: Gig;
  role: UserRole; 
  onAction: (action: string, gigId: string) => void;
}> = ({ gig, role, onAction }) => {

  const handleAction = (action: string) => {
    onAction(action, gig.id);
  };

  if (role === UserRole.ADMIN || role === UserRole.SYSTEM_ADMIN_LIVE) {
    return (
        <div className="space-x-2">
          <ActionButton onClick={() => handleAction('delete')} className="bg-red-500/20 text-red-300 hover:bg-red-500/40">Delete</ActionButton>
        </div>
      );
  }
  
  return <></>;
};

const RosterBookingRealDataTable = ({ currentUserRole, currentUserId }: { currentUserRole: UserRole, currentUserId: string }) => {
  const [gigs, setGigs] = useState<Gig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRealData, setShowRealData] = useState(false);
  const { notify } = useToast();
  const { addLog } = useLogger();

  useEffect(() => {
    if (showRealData) {
      const fetchData = async () => {
        setIsLoading(true);
        try {
          const response = await fetch(`${API_BASE_URL}/api/gigs`);
          if (!response.ok) {
            throw new Error(`Failed to fetch gigs: ${response.statusText}`);
          }
          const data: Gig[] = await response.json();
          setGigs(data);
        } catch (e) {
          const errorMessage = (e as Error).message;
          setError(errorMessage);
          notify(errorMessage, 'error');
        } finally {
          setIsLoading(false);
        }
      };
      fetchData();
    }
  }, [notify, showRealData]);

  const handleAction = async (action: string, gigId: string) => {
    if (action === 'delete') {
        if (!window.confirm('Are you sure you want to delete this gig?')) {
            return;
        }
        try {
            const response = await fetch(`${API_BASE_URL}/api/gigs/${gigId}`, { 
                method: 'DELETE',
                credentials: 'include',
            });

            if (response.status === 204) {
                setGigs(currentGigs => currentGigs.filter(g => g.id !== gigId));
                notify(`Gig ${gigId} was successfully deleted.`, 'success');
                addLog(
                  `Gig ${gigId} was deleted by user ${currentUserId}`,
                  LogLevel.INFO,
                  LogCategory.GIG,
                  { gigId, deletedBy: currentUserId }
                );
            } else {
                const errorData = await response.json().catch(() => ({ error: 'Failed to delete gig' }));
                throw new Error(errorData.error || 'Failed to delete gig');
            }
        } catch (err) {
            const errorMessage = (err as Error).message;
            notify(errorMessage || 'An error occurred while deleting the gig.', 'error');
            addLog(
              `Failed to delete gig ${gigId}`,
              LogLevel.ERROR,
              LogCategory.GIG,
              { gigId, error: errorMessage }
            );
        }
    }
  };
  
  const handleDecision = (id: string, decision: 'Confirmed' | 'Cancelled') => {
    // This is for the mock data
    console.log(`Booking ${id} has been ${decision}`);
  };

  const getVenueName = (venue: User | ArtistProfile | VenueProfile) => {
    if ('companyName' in venue && venue.companyName) {
        return venue.companyName;
    }
    if ('name' in venue && venue.name) {
        return venue.name;
    }
    return 'N/A';
  };

  if (isLoading && showRealData) return <div className="text-center p-8 text-kala-400">Loading gig data...</div>;
  if (error && showRealData) return <div className="text-center p-8 text-red-500 bg-red-500/10 rounded-lg">Error: {error}</div>;

  return (
    <div className="bg-kala-900 text-white rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Roster Booking Data</h2>
            <Button onClick={() => setShowRealData(!showRealData)}>
                {showRealData ? 'Show Mock Data' : 'Show Real Data'}
            </Button>
        </div>
        {showRealData ? (
            <Table className="bg-kala-900 text-white rounded-lg p-6">
                <TableCaption>Real-time roster booking data.</TableCaption>
                <TableHeader>
                    <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Artist</TableHead>
                    <TableHead>Venue</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {gigs.map((gig) => (
                    <TableRow key={gig.id}>
                        <TableCell>{gig.title}</TableCell>
                        <TableCell>{gig.artist?.name || 'N/A'}</TableCell>
                        <TableCell>{gig.venue ? getVenueName(gig.venue) : 'N/A'}</TableCell>
                        <TableCell>{new Date(gig.date).toLocaleDateString()}</TableCell>
                        <TableCell>{gig.status}</TableCell>
                        <TableCell className="text-right">
                            <TableActions gig={gig} role={currentUserRole} onAction={handleAction} />
                        </TableCell>
                    </TableRow>
                    ))}
                </TableBody>
            </Table>
        ) : (
            <Table className="bg-kala-900 text-white rounded-lg p-6">
                <TableCaption>A list of example roster bookings.</TableCaption>
                <TableHeader>
                    <TableRow>
                        <TableHead>Booking ID</TableHead>
                        <TableHead>Artist</TableHead>
                        <TableHead>Event</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                {mockBookings.map((booking) => (
                    <TableRow key={booking.id}>
                    <TableCell>{booking.id}</TableCell>
                    <TableCell>{booking.artist}</TableCell>
                    <TableCell>{booking.event}</TableCell>
                    <TableCell>{booking.status}</TableCell>
                    <TableCell>
                        {booking.status === 'Pending' && (
                        <div className="flex space-x-2">
                            <Button onClick={() => handleDecision(booking.id, 'Confirmed')} className="bg-green-500 hover:bg-green-600">Confirm</Button>
                            <Button onClick={() => handleDecision(booking.id, 'Cancelled')} className="bg-red-500 hover:bg-red-600">Cancel</Button>
                        </div>
                        )}
                    </TableCell>
                    </TableRow>
                ))}
                </TableBody>
            </Table>
        )}
    </div>
  );
};

export default RosterBookingRealDataTable;
